import { AlertCriterion, AlertCriterionKey } from '@interfaces/models/alertCriterion';

const CATEGORY_ID = 'idCategory' as AlertCriterionKey;
const SUBCATEGORY_ID = 'idSubcategory' as AlertCriterionKey;

const ALLOWED_URL_CATEGORY_KEYS = new Set(['categoryParent', 'category']);

const updateUniversalLinkWithSubcategory = (link: string, criteria: AlertCriterion[]): string => {
  if (!criteria || criteria.length === 0) {
    return link;
  }

  // Extract subcategory IDs from criteria
  const subcategoryIds = criteria
    .filter((criterion) => criterion.key === SUBCATEGORY_ID)
    .flatMap((criterion) => criterion.value);

  // If no subcategory IDs are found, return the original link
  if (subcategoryIds.length === 0) {
    return link;
  }

  // Find the part of the link that contains "_=" and capture everything until the next key=value pair or end of string
  const regex = /_=([^_#]+(?:#[^_#]+)*)(?=_|$)/; // Match everything after "_=" until another key=value pair or end of string
  const match = link.match(regex);

  if (match && match[1]) {
    // Extract the existing IDs after _= and split them by "-"
    const segments = match[1].split('-');

    // For each segment, split by "#" and extract the numeric part after "#"
    const existingIds = segments.map((segment) => segment.split('#').pop()).filter(Boolean);

    // Check if the extracted IDs match the provided subcategoryIds
    const isValid =
      existingIds.some((id) => subcategoryIds.includes(String(id))) &&
      subcategoryIds.some((id) => existingIds.includes(String(id)));

    if (isValid) {
      // Replace "_=" with "_subcategory=" while preserving the "#" or end of string
      return link.replace(regex, `_subcategory=${match[1]}`);
    }
  }

  // If no match or validity check fails, return the original link
  return link;
};

export const getUpdatedUniversalLink = (links, criteria) => {
  if (!links?.universalLink || !criteria || criteria.length === 0) {
    return links?.universalLink || '';
  }

  const hasCategoryAndSubcategory =
    criteria.some((c) => c.key === CATEGORY_ID) && criteria.some((c) => c.key === SUBCATEGORY_ID);

  const hasCategoryFields = [...ALLOWED_URL_CATEGORY_KEYS].every((key) => links.universalLink.includes(key));

  const isSubcategoryMissing = !links.universalLink.includes('_subcategory') && links.universalLink.includes('_=');

  const updatedUniversalLink =
    hasCategoryAndSubcategory && hasCategoryFields && isSubcategoryMissing
      ? updateUniversalLinkWithSubcategory(links.universalLink, criteria)
      : links.universalLink;

  // TEMP: TCRM:4727 and TCRM:4737: Until BE adds sorting to the universal link, we need to add it here
  const updateLinkWithSortOrder = new URL(updatedUniversalLink);
  const searchParams = new URLSearchParams(updateLinkWithSortOrder.search);

  // Check if sortBy already exists
  if (!searchParams.has('sortBy')) {
    searchParams.append('sortBy', '3');
  }

  // Reconstruct the full URL with updated query params
  updateLinkWithSortOrder.search = searchParams.toString();
  return updateLinkWithSortOrder.toString();
};
